/**
 * Static Mesh Family Renderer
 *
 * Draws BIMElements whose geometry comes from a baked IFC asset (see
 * services/ifc). The heavy vertex data lives in IndexedDB; this component
 * resolves it once per assetId into a THREE.BufferGeometry, caches it in
 * module scope (multiple placed copies share one geometry), and walks the
 * parts as individual meshes — same group-walk pattern the curtain wall
 * renderer uses. Plan/hidden-line/wireframe views overlay edges like every
 * other category renderer.
 */

import { useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { loadIfcAsset, IfcAssetRecord } from '../services/ifc/IfcAssetStore';
import { InkEdges } from './InkEdges';
import { BACKFACE_DEFINES } from './backfaceTint';

/** Shared geometry cache: assetId → built THREE resources (never freed per-session). */
const geometryCache = new Map<string, IfcAssetRecord | null>();

function buildAssetGeometries(asset: IfcAssetRecord): THREE.BufferGeometry[] {
    return asset.parts.map((part) => {
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(part.positions, 3));
        geo.setAttribute('normal', new THREE.BufferAttribute(part.normals, 3));
        geo.setIndex(new THREE.BufferAttribute(part.indices, 1));
        geo.computeBoundingSphere();
        return geo;
    });
}

/** Resolved asset payload: geometries + authored part colours (null while loading/pruned). */
interface ResolvedIfcAsset {
    geometries: THREE.BufferGeometry[];
    colors: [number, number, number][];
}

/**
 * Shared asset resolution hook: loads the baked mesh from IndexedDB once per
 * assetId (module-scope cache — multiple placed copies share one geometry)
 * and returns ready-to-render THREE resources.
 */
export function useIfcAsset(assetId: string): ResolvedIfcAsset | null {
    const invalidate = useThree((s) => s.invalidate);
    const [resolved, setResolved] = useState<ResolvedIfcAsset | null>(() => {
        const cached = geometryCache.get(assetId);
        return cached ? { geometries: buildAssetGeometries(cached), colors: cached.parts.map((p) => p.color) } : null;
    });

    useEffect(() => {
        let cancelled = false;
        const cached = geometryCache.get(assetId);
        if (cached !== undefined) {
            setResolved(cached ? { geometries: buildAssetGeometries(cached), colors: cached.parts.map((p) => p.color) } : null);
            return;
        }
        void (async () => {
            const asset = await loadIfcAsset(assetId);
            geometryCache.set(assetId, asset);
            if (cancelled) return;
            setResolved(asset ? { geometries: buildAssetGeometries(asset), colors: asset.parts.map((p) => p.color) } : null);
            invalidate();
        })();
        return () => { cancelled = true; };
    }, [assetId, invalidate]);

    return resolved;
}

interface StaticMeshFamilyData {
    kind: 'ifc-static';
    assetId: string;
    position: [number, number, number];
    rotation?: number; // degrees around Z (app frame)
}

export function isStaticMeshGeometryData(g: any): g is StaticMeshFamilyData {
    return g?.kind === 'ifc-static' && typeof g?.assetId === 'string';
}

export function StaticMeshFamilyRenderer({ element, isSelected, viewType, viewMode }: {
    element: { id: string; geometryData: StaticMeshFamilyData },
    isSelected: boolean,
    viewType: string,
    viewMode: string,
}) {
    const { assetId, position, rotation } = element.geometryData;
    const resolved = useIfcAsset(assetId);
    const geometries = resolved?.geometries ?? null;
    const colors = resolved?.colors ?? [];

    const edgeStyle = useMemo(() => {
        const isPlan = viewType === 'plan';
        const isHiddenLine = viewMode === 'hiddenLine';
        const isWireframe = viewMode === 'wireframe';
        return isPlan || isHiddenLine || isWireframe;
    }, [viewType, viewMode]);

    if (!geometries || geometries.length === 0) return null;

    const rotZ = rotation ? THREE.MathUtils.degToRad(rotation) : 0;
    // IFC and the app scene are both Z-up: the baked mesh needs no axis flip,
    // only yaw around Z for the placement rotation.
    return (
        <group position={position} rotation={[0, 0, rotZ]} userData={{ id: element.id }}>
            {geometries.map((geo, i) => (
                <mesh key={i} geometry={geo}>
                    <meshStandardMaterial
                        color={colors[i] ? new THREE.Color().setRGB(colors[i][0], colors[i][1], colors[i][2]) : '#9aa0a6'}
                        metalness={0}
                        roughness={0.85}
                        side={THREE.DoubleSide}
                        defines={BACKFACE_DEFINES}
                    />
                    {(edgeStyle || isSelected) && (
                        <InkEdges isSelected={isSelected} />
                    )}
                </mesh>
            ))}
        </group>
    );
}
